import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatNumber } from '../utils/formatters';
import './BurnHistoryModal.css';
import SoundManager from '../services/SoundManager';

interface BurnHistoryItem {
  id: number;
  amount: number;           // или amount_burned
  amount_burned?: number;   // с бэкенда
  burnedAt?: string;        // или burn_date
  burn_date?: string;       // с бэкенда
  transaction_hash?: string;
  description?: string;
  token_price?: number;     // цена токена на момент сжигания
  usd_value?: number;       // стоимость в USD
}

interface BurnStats {
  totalBurned: number;
  totalBurnEvents: number;
  avgBurnAmount: number;
  lastBurnDate: string | null;
}

interface BurnHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BurnHistoryModal: React.FC<BurnHistoryModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const [history, setHistory] = useState<BurnHistoryItem[]>([]);
  const [stats, setStats] = useState<BurnStats>({
    totalBurned: 0,
    totalBurnEvents: 0,
    avgBurnAmount: 0,
    lastBurnDate: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (isOpen) {
      SoundManager.playModalOpen();
      fetchBurnHistory();
      fetchBurnStats();
    }
  }, [isOpen, page]);

  if (!isOpen) return null;

  const fetchBurnHistory = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/burn/history?page=${page}&limit=20`);
      if (response.data.success) {
        const newHistory = response.data.data.history;

        if (page === 1) {
          setHistory(newHistory);
        } else {
          setHistory(prev => [...prev, ...newHistory]);
        }

        setHasMore(response.data.data.hasMore);
      }
    } catch (error) {
      console.error('Ошибка загрузки истории burn:', error);
      setHistory([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBurnStats = async () => {
    try {
      const response = await api.get('/burn/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики burn:', error);
      setStats({
        totalBurned: 0,
        totalBurnEvents: 0,
        avgBurnAmount: 0,
        lastBurnDate: null
      });
    }
  };

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content burn-history-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('burn.historyTitle')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Статистика */}
          <div className="burn-stats-grid">
            <div className="burn-stat-card">
              <div className="stat-icon">🔥</div>
              <div className="stat-content">
                <div className="stat-label">{t('burn.totalBurned')}</div>
                <div className="stat-value">{formatNumber(stats.totalBurned, 0)} SPERM</div>
              </div>
            </div>

            <div className="burn-stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-label">{t('burn.burnOperations')}</div>
                <div className="stat-value">{stats.totalBurnEvents}</div>
              </div>
            </div>

            <div className="burn-stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <div className="stat-label">{t('burn.avgAmount')}</div>
                <div className="stat-value">{formatNumber(stats.avgBurnAmount, 0)} SPERM</div>
              </div>
            </div>

            <div className="burn-stat-card">
              <div className="stat-icon">⏰</div>
              <div className="stat-content">
                <div className="stat-label">{t('burn.lastBurn')}</div>
                <div className="stat-value small">
                  {stats.lastBurnDate
                    ? new Date(stats.lastBurnDate).toLocaleDateString(locale)
                    : t('burn.noData')}
                </div>
              </div>
            </div>
          </div>

          {/* История операций */}
          <div className="burn-history-section">
            <h3 className="section-title">{t('burn.operationsHistory')}</h3>

            {history.length === 0 && !isLoading ? (
              <div className="no-history">
                <span className="no-history-icon">📭</span>
                <p>{t('burn.historyEmpty')}</p>
              </div>
            ) : (
              <div className="burn-history-list">
                {history.map((item) => {
                  // Поддержка обоих форматов полей с бэкенда
                  const burnAmount = item.amount || item.amount_burned || 0;
                  const burnDate = item.burnedAt || item.burn_date;
                  const usdValue = item.usd_value || 0;

                  return (
                    <div key={item.id} className="burn-history-item">
                      <div className="burn-item-header">
                        <div className="burn-item-icon">🔥</div>
                        <div className="burn-item-main">
                          <div className="burn-item-amount">
                            {formatNumber(burnAmount, 2)} SPERM
                            {usdValue > 0 && (
                              <span className="burn-usd-value"> (${formatNumber(usdValue, 2)})</span>
                            )}
                          </div>
                          <div className="burn-item-date">
                            {burnDate ? new Date(burnDate).toLocaleString(locale, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : t('burn.noData')}
                          </div>
                        </div>
                      </div>

                      {/* TX Hash - кликабельная ссылка */}
                      {item.transaction_hash && (
                        <div
                          className="burn-tx-link"
                          onClick={() => window.open(`https://tonviewer.com/transaction/${item.transaction_hash}`, '_blank')}
                        >
                          <span className="tx-icon">🔗</span>
                          <span className="tx-text">{t('burn.viewOnBlockchain') || 'Посмотреть в блокчейне'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Кнопка загрузки еще */}
            {hasMore && (
              <button
                className="load-more-btn"
                onClick={loadMore}
                disabled={isLoading}
              >
                {isLoading ? t('common.loading') : t('burn.loadMore')}
              </button>
            )}
          </div>

          {/* Информация */}
          <div className="burn-info-section">
            <h3>{t('burn.aboutMechanism')}</h3>
            <ul>
              <li>{t('burn.info1')}</li>
              <li>{t('burn.info2')}</li>
              <li>{t('burn.info3')}</li>
              <li>{t('burn.info4')}</li>
              <li>{t('burn.info5')}</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default BurnHistoryModal;
